import React, { useState, useEffect, useContext } from "react";
import UserContext from "../../contexts/UserContext";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import displayError from "../../utils/ErrorHandler";

const AddItemInSupplier = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { supplier } = location.state || {};
  const { user, setUser } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [supplierItems, setSupplierItems] = useState([]);
  const [isCategoryFilterEnabled, setIsCategoryFilterEnabled] = useState(false);

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
      if (user) {
        fetchSupplierItems();
      }
    }
    startup();
  }, [user]);

  useEffect(() => {
    if (isCategoryFilterEnabled) {
      fetchItems();
    } else {
      setItems([]);
    }
  }, [isCategoryFilterEnabled]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/items/getAll`,
        { withCredentials: true }
      );
      setItems(response.data.data);
      const uniqueCategories = [
        ...new Set(response.data.data.map((item) => item.category)),
      ];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchSupplierItems = async () => {
    setSupplierItems(supplier.items);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 0) {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/findItems?q=${query}`,
        { withCredentials: true }
      );
      setItems(response.data);
    } else if (isCategoryFilterEnabled) {
      fetchItems();
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleAssignItems = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URI}/api/supplier/${supplier._id}/assignItems`,
        { itemIds: selectedItems },
        { withCredentials: true }
      );
      navigate(-2);
    } catch (error) {
      console.error("Error assigning items:", error);
    }
  };

  const isItemAssigned = (itemId) => {
    return supplierItems.some((item) => item._id === itemId);
  };

  const filteredItems = items.filter((item) => {
    return (
      !isCategoryFilterEnabled ||
      !selectedCategory ||
      item.category === selectedCategory
    );
  });

  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-lime-800 flex items-center justify-between w-full p-4 text-white fixed z-10">
        <div className="mr-4 cursor-pointer" onClick={() => navigate(-1)}>
          <span className="text-xl text-white">←</span>
        </div>
        <h2 className="text-lg md:text-xl font-semibold text-center flex-grow">
          Add Items to {supplier ? supplier.company : "Supplier"}
        </h2>
      </div>
      <div className="p-4 pt-[75px]">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 pb-3">
            Search Item{" "}
            <span className="text-yellow-600 font-semibold">
              Please Type any word to fetch details from Items List Or Click Filter by Category to get All Items List
            </span>
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Type to search..."
            className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div className="mb-4 flex items-center">
          <label className="custom-checkbox">
            <input
              type="checkbox"
              checked={isCategoryFilterEnabled}
              onChange={() =>
                setIsCategoryFilterEnabled(!isCategoryFilterEnabled)
              }
            />
            <span className="checkmark"></span>
          </label>
          <span className="text-lg font-medium text-gray-700">
            Filter by Category
          </span>
        </div>
        {isCategoryFilterEnabled && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto">
          {filteredItems.map((item) => (
            <div key={item._id} className="flex items-center mb-2">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item._id)}
                  onChange={() => handleItemSelect(item._id)}
                  disabled={isItemAssigned(item._id)}
                />
                <span className="checkmark"></span>
              </label>
              <div className="flex-grow">
                <h4 className="text-sm font-medium">{item.name}</h4>
                <p className="text-xs text-gray-500">{item.category}</p>
              </div>
              {isItemAssigned(item._id) && (
                <span className="ml-4 px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded-full">
                  Assigned
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleAssignItems}
            className="bg-fmb text-white py-2 px-5 rounded-full text-lg font-normal hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
          >
            Assign Selected Items
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemInSupplier;